/* eslint-disable no-param-reassign, no-shadow, react/prop-types, no-unused-vars */
import React from 'react';
import { Box, IconButton, Paper, Tooltip, makeStyles, useTheme } from '@material-ui/core';
import { Empty } from 'antd';
import { FunctionField, ListContextProvider, TextField, useTranslate } from 'react-admin';
import { Delete } from '@material-ui/icons';
import Pagination from '../../../../base/components/customizable-colums/Pagination';
import Datagrid from '../../../../base/components/ra/data-grid';
import { DateField } from '../../../../base/components/ra/fields';
import AmountField from '../../../../base/components/ra/fields/amount.field';
import { DATE_DISPLAY_FORMAT } from '../../../../constant';
import resourceSlug from '../../../../constant/resource-slug';
import { usePayment } from '../../hooks';

const InvoiceTable = () => {
  const theme = useTheme();
  const { invoices = {}, loading, loaded, error, setInvoices, invoiceIds: ids = [] } = usePayment();
  const t = useTranslate();
  const classes = useClasses();

  return error ? (
    <Box mt={3}>
      <Empty description={false} />
    </Box>
  ) : (
    <ListContextProvider
      value={{
        ids: Object.keys(invoices || {}),
        data: invoices,
        loading,
        basePath: '',
        selectedIds: [],
        currentSort: {
          field: null,
          order: null,
        },
        displayedFilters: null,
        filterValues: null,
        hasCreate: null,
        hideFilter: null,
        loaded,
        onSelect: null,
        onToggleItem: null,
        onUnselectItems: null,
        page: 1,
        perPage: 25,
        resource: '',
        setFilters: null,
        setPage: () => {},
        setPerPage: () => {},
        setSort: null,
        showFilter: null,
        total: Object.keys(invoices || {}).length,
      }}
    >
      <Paper className={classes.paper}>
        <Datagrid
          loadingProps={{
            nbFakeLines: ids?.length,
          }}
          rowClick={(id) => `/${resourceSlug.INVOICE}/${id}/show`}
        >
          {ids?.length > 1 && (
            <FunctionField
              label="ra.field.action"
              sortable={false}
              render={(record) => (
                <Tooltip title={t('ra.action.remove')}>
                  <Box
                    component={IconButton}
                    size="small"
                    sx={{
                      color: `${theme.palette.error.main} !important`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const newInvoices = {
                        ...invoices,
                      };
                      delete newInvoices[record.id];

                      setInvoices(newInvoices);
                    }}
                  >
                    <Delete />
                  </Box>
                </Tooltip>
              )}
            />
          )}
          <TextField
            label="resources.invoice.fields.invoiceNo"
            sortable={false}
            source="invoiceNo"
            cellClassName={classes.invoiceNo}
          />
          <DateField
            source="invoiceDate"
            label="resources.invoice.fields.invoiceDate"
            dateFormat={DATE_DISPLAY_FORMAT}
            sx={{
              minWidth: 90,
            }}
          />
          <AmountField
            source="invoiceAmount"
            sortable={false}
            cellClassName={classes.amount}
            label="resources.invoice.fields.amount"
            symbol={(record) => record.currencyCode}
          />
        </Datagrid>
        <Pagination />
      </Paper>
    </ListContextProvider>
  );
};

export default React.memo(InvoiceTable);

const useClasses = makeStyles((theme) => ({
  paper: {
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 8,
    padding: '0px 16px',
    '& table': {
      border: 'none',
      flexGrow: 1,
      '& th, & td': {
        borderRight: 'none',
        borderBottomColor: theme.palette.grey[300],
      },
      '& td': {
        padding: '10px 16px',
      },
      '& th': {
        padding: 16,
      },
      '& th:not(:last-child)': {
        '&:before': {
          position: 'absolute',
          top: '50%',
          right: 0,
          width: 1,
          height: '1.6em',
          backgroundColor: theme.palette.grey[300],
          transform: 'translateY(-50%)',
          content: '""',
        },
      },
    },
    '& *[class*="MuiTablePagination-root"]': {
      fontWeight: 500,
      marginTop: 'auto',
    },
  },
  invoiceNo: {
    minWidth: 200,
  },
  date: {
    minWidth: 90,
  },
  amount: {
    minWidth: 200,
  },
}));
